import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import {
  saveAdminPlanFeature,
  deleteAdminPlanFeature,
  type PlanFeaturePayload,
} from "@/services/adminService";
import type { AdminPlanFeature, AdminPlansBundle } from "@/types/admin";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import {
  adminCard,
  adminTh,
  adminTd,
  adminBtnPrimary,
  adminBtnGhost,
  adminBtnDanger,
  adminIconBtn,
  adminInput,
  adminLabel,
} from "@/pages/portal/admin/config/ui";

interface FeatureForm {
  feature: string;
  freeValue: string;
  businessValue: string;
  premiumValue: string;
  rowOrder: number;
}

const EMPTY_FORM: FeatureForm = {
  feature: "",
  freeValue: "—",
  businessValue: "—",
  premiumValue: "—",
  rowOrder: 1,
};

interface ComparisonTabProps {
  token: string | null;
  data: AdminPlansBundle;
  setData: Dispatch<SetStateAction<AdminPlansBundle>>;
  loading: boolean;
}

/** Admin tab — the "Everything included, side by side" comparison table rows. */
export default function ComparisonTab({ token, data, setData, loading }: ComparisonTabProps) {
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<FeatureForm | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminPlanFeature | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = [...data.features].sort((a, b) => a.rowOrder - b.rowOrder);
    if (!q) return rows;
    return rows.filter((f) => f.feature.toLowerCase().includes(q));
  }, [data.features, search]);

  function openCreate() {
    const nextOrder = data.features.reduce((max, f) => Math.max(max, f.rowOrder), 0) + 1;
    setForm({ ...EMPTY_FORM, rowOrder: nextOrder });
    setEditId(null);
  }

  function openEdit(row: AdminPlanFeature) {
    setForm({
      feature: row.feature,
      freeValue: row.freeValue,
      businessValue: row.businessValue,
      premiumValue: row.premiumValue,
      rowOrder: row.rowOrder,
    });
    setEditId(row.id);
  }

  function setField<K extends keyof FeatureForm>(key: K, value: FeatureForm[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function submit() {
    if (!form?.feature.trim()) return;
    const payload: PlanFeaturePayload = {
      feature: form.feature.trim(),
      freeValue: form.freeValue,
      businessValue: form.businessValue,
      premiumValue: form.premiumValue,
      rowOrder: form.rowOrder,
    };

    setBusy(true);
    try {
      await saveAdminPlanFeature(token, payload, editId ?? undefined);
      if (editId) {
        setData((prev) => ({
          ...prev,
          features: prev.features.map((f) => (f.id === editId ? { ...f, ...payload } as AdminPlanFeature : f)),
        }));
      } else {
        setData((prev) => ({
          ...prev,
          features: [...prev.features, { id: Date.now(), ...(payload as Omit<AdminPlanFeature, "id">) } as AdminPlanFeature],
        }));
      }
      setForm(null);
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteAdminPlanFeature(token, deleteTarget.id);
      setData((prev) => ({ ...prev, features: prev.features.filter((f) => f.id !== deleteTarget.id) }));
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder="Search comparison rows…" />
        <button type="button" onClick={openCreate} className={adminBtnPrimary}>
          <i className="ri-add-line text-sm" /> New row
        </button>
        <span className="ml-auto text-xs text-foreground-500">
          {loading ? "Loading…" : `${data.features.length} rows`}
        </span>
      </div>

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-table-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No comparison rows yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead className="border-b border-background-200">
                <tr>
                  <th className={adminTh}>Order</th>
                  <th className={adminTh}>Feature</th>
                  <th className={`${adminTh} text-center`}>Free</th>
                  <th className={`${adminTh} text-center`}>Business</th>
                  <th className={`${adminTh} text-center`}>Premium</th>
                  <th className={`${adminTh} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filtered.map((row) => (
                  <tr key={row.id} className="hover:bg-background-100/40">
                    <td className={`${adminTd} tabular-nums`}>{row.rowOrder}</td>
                    <td className={`${adminTd} font-semibold text-foreground-900`}>{row.feature}</td>
                    <td className={`${adminTd} text-center`}>{row.freeValue}</td>
                    <td className={`${adminTd} text-center`}>{row.businessValue}</td>
                    <td className={`${adminTd} text-center`}>{row.premiumValue}</td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => openEdit(row)} className={adminIconBtn} title="Edit row">
                          <i className="ri-pencil-line text-base" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(row)}
                          className={adminIconBtn}
                          title="Delete row"
                        >
                          <i className="ri-delete-bin-line text-base text-rose-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {form && (
        <AdminModal
          title={editId ? "Edit comparison row" : "New comparison row"}
          subtitle="One feature, compared across the three plans"
          onClose={() => setForm(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setForm(null)} className={adminBtnGhost}>
                Cancel
              </button>
              <button
                type="button"
                disabled={!form.feature.trim() || busy}
                onClick={submit}
                className={adminBtnPrimary}
              >
                {editId ? "Save changes" : "Create row"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className={adminLabel} htmlFor="cf-feature">Feature</label>
              <input
                id="cf-feature"
                type="text"
                value={form.feature}
                onChange={(e) => setField("feature", e.target.value)}
                placeholder="e.g. Photos per listing"
                className={adminInput}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className={adminLabel} htmlFor="cf-free">Free</label>
                <input
                  id="cf-free"
                  type="text"
                  value={form.freeValue}
                  onChange={(e) => setField("freeValue", e.target.value)}
                  className={adminInput}
                />
              </div>
              <div>
                <label className={adminLabel} htmlFor="cf-biz">Business</label>
                <input
                  id="cf-biz"
                  type="text"
                  value={form.businessValue}
                  onChange={(e) => setField("businessValue", e.target.value)}
                  className={adminInput}
                />
              </div>
              <div>
                <label className={adminLabel} htmlFor="cf-prem">Premium</label>
                <input
                  id="cf-prem"
                  type="text"
                  value={form.premiumValue}
                  onChange={(e) => setField("premiumValue", e.target.value)}
                  className={adminInput}
                />
              </div>
            </div>
            <div>
              <label className={adminLabel} htmlFor="cf-order">Row order</label>
              <input
                id="cf-order"
                type="number"
                min={0}
                value={form.rowOrder}
                onChange={(e) => setField("rowOrder", Number(e.target.value) || 0)}
                className={`${adminInput} max-w-[160px]`}
              />
            </div>
            <p className="text-xs text-foreground-500">
              Use an em dash “—” for “not included” and “Included” to show a check mark.
            </p>
          </div>
        </AdminModal>
      )}

      {deleteTarget && (
        <AdminModal
          title="Delete comparison row"
          subtitle={deleteTarget.feature}
          onClose={() => setDeleteTarget(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>
                Cancel
              </button>
              <button type="button" disabled={busy} onClick={confirmDelete} className={adminBtnDanger}>
                <i className="ri-delete-bin-line text-sm" /> Delete row
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">
            Delete the <strong>{deleteTarget.feature}</strong> row from the comparison table?
          </p>
        </AdminModal>
      )}
    </div>
  );
}